import React from 'react';
import { Box, Divider, IconButton, List, ListItemButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import { ReelElement, ReelElementType } from './reelCanvas.types';

/** What the panel needs from a layer; Reel Maker elements and Screen Composer widgets both fit. */
export interface LayerItem {
    id: string;
    name: string;
    type: string;
    visible: boolean;
    locked: boolean;
}

export interface LayersPanelProps<T extends LayerItem = ReelElement> {
    /** Bottom → top (z-order). Displayed top-most first. */
    elements: T[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    onMove: (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => void;
    onToggleVisible: (id: string) => void;
    onToggleLocked: (id: string) => void;
    onDelete: (id: string) => void;
    onClose: () => void;
    /** Icon per layer; defaults to the Reel Maker element icons. */
    renderIcon?: (el: T) => React.ReactNode;
    /** Second line per layer; defaults to Reel Maker's z-order and pixel size. */
    renderSubtitle?: (el: T, index: number) => string;
    emptyText?: string;
}

const TYPE_ICON: Record<ReelElementType, React.ReactNode> = {
    media: <PermMediaOutlinedIcon fontSize="inherit" />,
    text: <TextFieldsIcon fontSize="inherit" />,
    container: <CropSquareIcon fontSize="inherit" />,
    audio: <AudiotrackIcon fontSize="inherit" />,
};

const reelIcon = (el: LayerItem) => TYPE_ICON[el.type as ReelElementType] ?? null;
const reelSubtitle = (el: LayerItem, index: number) => {
    const sized = el as LayerItem & { width?: number; height?: number };
    return el.type === 'audio' ? 'Timeline only · audio' : `z${index + 1} · ${sized.width}×${sized.height}`;
};

/** Floating layer stack: select, reorder (z-index), show/hide, lock and delete SideKick components. */
export const LayersPanel = <T extends LayerItem = ReelElement>({
    elements,
    selectedId,
    onSelect,
    onMove,
    onToggleVisible,
    onToggleLocked,
    onDelete,
    onClose,
    renderIcon = reelIcon,
    renderSubtitle = reelSubtitle,
    emptyText = 'No layers yet. Use the SideKick menu to add media, text or a container.',
}: LayersPanelProps<T>) => {
    const theme = useTheme();
    const topFirst = [...elements].reverse();
    const count = elements.length;

    return (
        <Paper
            elevation={6}
            aria-label="Layers"
            onPointerDown={(e) => e.stopPropagation()}
            sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 30,
                width: 288,
                maxHeight: 'calc(100% - 24px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.96),
                backdropFilter: 'blur(6px)',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                    Layers
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {count}
                </Typography>
                <IconButton size="small" aria-label="Close layers" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Divider />
            {count === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    {emptyText}
                </Typography>
            ) : (
                <List dense disablePadding sx={{ overflowY: 'auto' }}>
                    {topFirst.map((el, displayIndex) => {
                        const index = count - 1 - displayIndex;
                        const isTop = index === count - 1;
                        const isBottom = index === 0;
                        const isSelected = el.id === selectedId;
                        return (
                            <ListItemButton
                                key={el.id}
                                selected={isSelected}
                                onClick={() => onSelect(isSelected ? null : el.id)}
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    gap: 0.5,
                                    alignItems: 'center',
                                    opacity: el.visible ? 1 : 0.55,
                                    '&.Mui-selected': { backgroundColor: alpha(theme.palette.secondary.main, 0.18) },
                                    '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.26) },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 18, color: 'text.secondary', width: 22 }}>
                                    {renderIcon(el)}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 700 : 500 }} title={el.name}>
                                        {el.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {renderSubtitle(el, index)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title="Bring to front">
                                        <span>
                                            <IconButton size="small" disabled={isTop} aria-label={`Bring ${el.name} to front`} onClick={() => onMove(el.id, 'top')}>
                                                <VerticalAlignTopIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Move up">
                                        <span>
                                            <IconButton size="small" disabled={isTop} aria-label={`Move ${el.name} up`} onClick={() => onMove(el.id, 'up')}>
                                                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                        <span>
                                            <IconButton size="small" disabled={isBottom} aria-label={`Move ${el.name} down`} onClick={() => onMove(el.id, 'down')}>
                                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Send to back">
                                        <span>
                                            <IconButton size="small" disabled={isBottom} aria-label={`Send ${el.name} to back`} onClick={() => onMove(el.id, 'bottom')}>
                                                <VerticalAlignBottomIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={el.visible ? 'Hide' : 'Show'}>
                                        <IconButton size="small" aria-label={`${el.visible ? 'Hide' : 'Show'} ${el.name}`} onClick={() => onToggleVisible(el.id)}>
                                            {el.visible ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={el.locked ? 'Unlock' : 'Lock'}>
                                        <IconButton size="small" aria-label={`${el.locked ? 'Unlock' : 'Lock'} ${el.name}`} onClick={() => onToggleLocked(el.id)}>
                                            {el.locked ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete layer">
                                        <IconButton size="small" color="error" aria-label={`Delete ${el.name}`} onClick={() => onDelete(el.id)}>
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
};

export default LayersPanel;
